import { Component, OnInit } from '@angular/core';
import { WordpressService } from '../../../services/wordpress.service';
import { FixerServiceCard, WhyBlinkCard } from '../../../models/service.model';

@Component({
  selector: 'app-oman-fixer',
  templateUrl: './oman-fixer.component.html',
  styleUrls: ['./oman-fixer.component.scss']
})
export class OmanFixerComponent implements OnInit {
  whyBlinkItems: WhyBlinkCard[] = [];
  whyBlinkLoading = true;
  whyBlinkError: string | null = null;
  whyBlinkSkeletonItems = Array.from({ length: 3 });

  fixerServices: FixerServiceCard[] = [];
  fixerServicesLoading = true;
  fixerServicesError: string | null = null;
  fixerServicesSkeletonItems = Array.from({ length: 6 });

  // This post type currently has no ACF icon field populated in WordPress (verified
  // against the live API response — acf is empty on every item), so icons fall back
  // to this slug map, then a keyword match, then a round-robin default — the exact
  // same fallback chain HomeThreeComponent already uses for "Our Services".
  private fixerServiceIconMap: Record<string, string> = {
    'logistics': 'bx bx-map-alt',
    'permits': 'bx bx-file',
    'local-crew': 'bx bx-group',
    'equipment-rental': 'bx bx-camera-movie',
    'transport': 'bx bx-bus',
    'accommodation': 'bx bx-hotel'
  };
  private fixerServiceDefaultIcons = [
    'bx bx-map-alt',
    'bx bx-file',
    'bx bx-group',
    'bx bx-camera-movie',
    'bx bx-bus',
    'bx bx-hotel'
  ];

  constructor(private wordpressService: WordpressService) { }

  ngOnInit(): void {
    this.loadWhyBlink();
    this.loadFixerServices();
  }

  private loadWhyBlink(): void {
    this.wordpressService.getWhyBlink().subscribe({
      next: items => {
        this.whyBlinkItems = items;
        this.whyBlinkLoading = false;
      },
      error: err => {
        this.whyBlinkError = this.formatError(err, 'Unable to fetch Why Blink content from WordPress.');
        this.whyBlinkLoading = false;
      }
    });
  }

  private loadFixerServices(): void {
    this.wordpressService.getFixerServices().subscribe({
      next: items => {
        this.fixerServices = items;
        this.fixerServicesLoading = false;
      },
      error: err => {
        this.fixerServicesError = this.formatError(err, 'Unable to fetch Fixer Services from WordPress.');
        this.fixerServicesLoading = false;
      }
    });
  }

  trackByWhyBlink(index: number, item: WhyBlinkCard): number {
    return item.id;
  }

  trackByFixerService(index: number, item: FixerServiceCard): number {
    return item.id;
  }

  getFixerServiceIconClass(item: FixerServiceCard, index: number): string {
    if (item.iconClass) {
      return item.iconClass;
    }

    const slug = (item.slug || '').toLowerCase();
    if (this.fixerServiceIconMap[slug]) {
      return this.fixerServiceIconMap[slug];
    }

    const title = item.title.toLowerCase();
    if (/logistic/.test(title)) {
      return 'bx bx-map-alt';
    }
    if (/permit/.test(title)) {
      return 'bx bx-file';
    }
    if (/crew|staff|talent/.test(title)) {
      return 'bx bx-group';
    }
    if (/equipment|rental|gear/.test(title)) {
      return 'bx bx-camera-movie';
    }
    if (/transport|vehicle|route/.test(title)) {
      return 'bx bx-bus';
    }
    if (/accommodation|hotel|lodging/.test(title)) {
      return 'bx bx-hotel';
    }

    return this.fixerServiceDefaultIcons[index % this.fixerServiceDefaultIcons.length];
  }

  private formatError(err: any, fallback: string): string {
    if (err?.status) {
      return `${err.status} ${err.statusText || 'Error'}${err.error?.message ? ' - ' + err.error.message : ''}`;
    }

    return err?.message || fallback;
  }

}
